import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { OFFERS_DATA } from "../../constant/OffersData";
import Eyebrow from "../common/Eyebrow";

const formatDate = (isoDate) =>
  new Date(isoDate).toLocaleDateString("en-IN", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });

const isOfferActive = (offer) => {
  const today = new Date().toISOString().slice(0, 10);
  return today >= offer.validFrom && today <= offer.validTo;
};

const Offers = () => {
  const navigate = useNavigate();
  const activeOffers = OFFERS_DATA.filter(isOfferActive);
  const pastOffers = OFFERS_DATA.filter((offer) => !isOfferActive(offer));

  return (
    <>
      <Helmet>
        <title>
          Current IT Offers & Deals | QuantaRock Systems
        </title>
        <meta
          name="description"
          content="See QuantaRock's current offers on servers, RAM, SSDs, colocation, and IT consultation. Limited-time deals for businesses across India, updated regularly."
        />
        <meta
          name="keywords"
          content="quantarock offers, IT hardware discount, server deals india, RAM SSD offer, data center offer, colocation discount, IT consultation free offer, quantarock deals"
        />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content="Current IT Offers & Deals | QuantaRock Systems" />
        <meta
          property="og:description"
          content="Limited-time offers on IT hardware, servers, and data center services from QuantaRock Systems."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://quantarock.in/offers" />
        <meta property="og:image" content="https://quantarock.in/og-image.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <link rel="canonical" href="https://quantarock.in/offers" />
      </Helmet>
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ItemList",
          itemListElement: activeOffers.map((offer, i) => ({
            "@type": "Offer",
            position: i + 1,
            name: offer.title,
            description: offer.description || offer.message,
            url: "https://quantarock.in/offers",
            validFrom: offer.validFrom,
            priceValidUntil: offer.validTo,
            seller: {
              "@type": "Organization",
              name: "QuantaRock Systems",
            },
          })),
        })}
      </script>

      <section className="min-h-screen bg-slate-50 px-6 py-20 md:px-12">
        <div className="mx-auto max-w-5xl">
          <div className="text-center">
            <Eyebrow>Offers &amp; updates</Eyebrow>
          </div>
          <h1 className="mt-3 text-center text-3xl font-bold tracking-tight text-[var(--website-primary-color)] md:text-4xl">
            Current IT Offers &amp; Deals
          </h1>
          <p className="mx-auto mt-4 max-w-2xl text-center text-slate-600">
            Live promotions on IT hardware, servers, and data center services.
            Check back regularly &mdash; new offers are posted here as soon as they go live.
          </p>

          <div className="mt-12">
            {activeOffers.length === 0 ? (
              <p className="rounded-2xl border border-slate-200 bg-white p-8 text-center text-slate-500">
                No active offers right now &mdash; check back soon.
              </p>
            ) : (
              <div className="grid gap-6">
                {activeOffers.map((offer) => (
                  <div
                    key={offer.id}
                    className="rounded-3xl border border-slate-200 bg-white p-8 shadow-sm"
                  >
                    <span className="inline-flex items-center rounded-full bg-[var(--website-secondary-color)] px-4 py-1.5 text-xs font-semibold text-[var(--on-secondary)]">
                      {offer.emoji && <span className="mr-1.5">{offer.emoji}</span>}
                      Active offer
                    </span>
                    <h2 className="mt-4 text-2xl font-bold text-[var(--website-primary-color)]">
                      {offer.title}
                    </h2>
                    {offer.description && (
                      <p className="mt-2 text-slate-600">{offer.description}</p>
                    )}
                    {offer.highlights && (
                      <ul className="mt-5 space-y-2 text-sm text-slate-700">
                        {offer.highlights.map((line, i) => (
                          <li key={i} className="flex gap-2">
                            <span className="text-[var(--website-secondary-color)]">
                              &#10003;
                            </span>
                            {line}
                          </li>
                        ))}
                      </ul>
                    )}
                    <p className="mt-5 text-sm text-slate-500">
                      Valid {formatDate(offer.validFrom)} &ndash; {formatDate(offer.validTo)}
                    </p>
                    <button
                      onClick={() => navigate(offer.ctaLink || "/contact")}
                      className="mt-5 inline-flex rounded-full bg-[var(--website-primary-color)] px-6 py-3 text-sm font-semibold text-white transition hover:bg-[var(--primary-hover)]"
                    >
                      {offer.ctaText || "Get in touch"}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {pastOffers.length > 0 && (
            <div className="mt-16">
              <h2 className="text-lg font-semibold text-[var(--website-primary-color)]">Past offers</h2>
              <div className="mt-5 grid gap-4">
                {pastOffers.map((offer) => (
                  <div
                    key={offer.id}
                    className="rounded-2xl border border-slate-200 bg-white/60 p-6 opacity-70"
                  >
                    <h3 className="font-semibold text-[var(--website-primary-color)]">{offer.title}</h3>
                    {offer.description && (
                      <p className="mt-1 text-sm text-slate-500">{offer.description}</p>
                    )}
                    <p className="mt-2 text-xs uppercase tracking-wide text-slate-400">
                      Ran {formatDate(offer.validFrom)} &ndash; {formatDate(offer.validTo)}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>
    </>
  );
};

export default Offers;
